
import OpenAI from 'openai';
import { ChatMessage } from '@/types/chat';
import { createChatMessages, handleChatError, delay } from './chatUtils';
import { initializeOpenAIClient } from './openai';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

export const getChatCompletion = async (
  openai: OpenAI,
  text: string,
  conversationHistory: ChatMessage[] = []
): Promise<string | null> => {
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      console.log(`Attempting chat completion (attempt ${attempt + 1})`);
      
      const messages = createChatMessages(text, conversationHistory);

      const completion = await openai.chat.completions.create({
        messages,
        model: "gpt-4o",
        temperature: 0.7,
        max_tokens: 500
      });

      const response = completion.choices[0]?.message?.content;
      if (!response) {
        console.log('No response content received');
        continue;
      }

      console.log('Successfully received response');
      return response;

    } catch (error) {
      console.error(`Error in attempt ${attempt + 1}:`, error);
      
      if (attempt === MAX_RETRIES - 1) {
        const errorMessage = handleChatError(error);
        throw new Error(errorMessage);
      }

      if (error.status === 429) {
        console.log(`Rate limited. Waiting ${RETRY_DELAY_MS}ms before retry...`);
        await delay(RETRY_DELAY_MS);
        continue;
      }

      throw error;
    }
  }

  return null;
};

// Alternative function that handles OpenAI client initialization internally
export const getChatCompletionWithText = async (
  text: string,
  conversationHistory: ChatMessage[] = [],
  language: string = 'es'
): Promise<string> => {
  try {
    const openai = await initializeOpenAIClient();
    if (!openai) {
      throw new Error('Failed to initialize OpenAI client');
    }

    const result = await getChatCompletion(openai, text, conversationHistory);
    return result || 'No response received';
  } catch (error) {
    console.error('Error in getChatCompletionWithText:', error);
    throw error;
  }
};
